import { FilterList, SelectFilter, Sort } from "@dash/filters";
import { Button } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import React, { useMemo, useState } from "react";
import { MdBugReport, MdSync } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager } from "@megaron/react-clients";
import {
  UnitTemplate,
  unitTemplateCategories,
  UnitTemplateCategory,
  unitTemplateComponentsSerializer,
} from "@megaron/shipping-contracts";

type UnitTemplateSortField = "id" | "unit" | "category" | "label" | "complexity" | "components";
type UnitTemplateSort = { field: UnitTemplateSortField; order: "ASC" | "DESC" } | undefined;

const tableColumns: TableHeadColumn<UnitTemplateSortField>[] = [
  { label: "ID", isSortable: true, sortFieldName: "id" },
  { label: "Jednostka", isSortable: true, sortFieldName: "unit" },
  { label: "Kategoria", isSortable: true, sortFieldName: "category" },
  { label: "Nazwa", isSortable: true, sortFieldName: "label" },
  { label: "Złożoność", isSortable: true, sortFieldName: "complexity" },
  { label: "Komponenty", isSortable: true, sortFieldName: "components" },
];

const sortOptions = [
  { label: "ID", value: "id" },
  { label: "Jednostka", value: "unit" },
  { label: "Kategoria", value: "category" },
  { label: "Nazwa", value: "label" },
  { label: "Złożoność", value: "complexity" },
  { label: "Komponenty", value: "components" },
] satisfies { label: string; value: UnitTemplateSortField }[];

export const UnitTemplatesHome: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const client = useClientManager("shipping");
  const [categories, setCategories] = useState<UnitTemplateCategory[]>([]);
  const [searchText, setSearchText] = useState("");
  const [units, setUnits] = useState<string[]>([]);
  const [component, setComponent] = useState<string | undefined>();
  const [descendantOf, setDescendantOf] = useState<string | undefined>();
  const [ancestorOf, setAncestorOf] = useState<string | undefined>();
  const [sort, setSort] = useState<UnitTemplateSort>();

  const templateOptionsQuery = client.unitTemplateQuery().useQuery({});
  const templatesQuery = client.unitTemplateQuery().useQuery({
    searchText: searchText || undefined,
    category: categories.length > 0 ? categories : undefined,
    unit: units.length > 0 ? units : undefined,
    component,
    descendantOf,
    ancestorOf,
  });
  const unitOptions = useMemo(
    () => [...new Set(templateOptionsQuery.data?.units.map((template) => template.unit) ?? [])].sort().map(toOption),
    [templateOptionsQuery.data?.units],
  );
  const componentOptions = useMemo(
    () =>
      [...new Set(templateOptionsQuery.data?.units.flatMap((template) => Object.keys(template.components)) ?? [])]
        .sort()
        .map(toOption),
    [templateOptionsQuery.data?.units],
  );

  const getTableRowCellsContent = (template: UnitTemplate): RowCellContent[] => [
    { cellCss: { fontFamily: "monospace", whiteSpace: "nowrap" }, element: template.id },
    { cellCss: { color: theme.colors.primary, whiteSpace: "nowrap" }, element: template.unit },
    { cellCss: { whiteSpace: "nowrap" }, element: template.category },
    { cellCss: { whiteSpace: "nowrap" }, element: template.label },
    { cellCss: { whiteSpace: "nowrap" }, element: template.complexity },
    {
      cellCss: { whiteSpace: "pre-line", maxWidth: "24rem", verticalAlign: "top" },
      element: unitTemplateComponentsSerializer.serialize(template.components),
    },
  ];

  return (
    <Page
      css={{
        maxWidth: "none",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <PageHeader
        actions={
          <div css={{ display: "flex", gap: "0.5rem" }}>
            <Button
              icon={<MdBugReport />}
              variant="outline"
              onClick={() => navigate(getMegaraxPath.uri("megarax://catalog/unit-templates/debug"))}
            >
              Debug kalkulatora
            </Button>
            <Button
              icon={<MdSync />}
              onClick={() =>
                navigate(getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: "sheetsUnitTemplate" }))
              }
            >
              Synchronizuj
            </Button>
          </div>
        }
      >
        Szablony jednostek
      </PageHeader>

      <SearchWithPaginationSticky onSearchChange={setSearchText} searchValue={searchText} hidePagination />

      <FilterList>
        <SelectFilter
          label="Kategorie"
          variant="multi-select"
          initiallySelectedValues={categories}
          options={unitTemplateCategories.map(toOption)}
          onSelectedChange={(options) => setCategories(options.map((option) => option.value as UnitTemplateCategory))}
        />
        <SelectFilter
          label="Jednostki"
          variant="multi-select"
          initiallySelectedValues={units}
          options={unitOptions}
          onSelectedChange={(options) => setUnits(options.map((option) => option.value))}
        />
        <SelectFilter
          label="Komponent"
          variant="single-select"
          initiallySelectedValues={component ? [component] : []}
          options={componentOptions}
          onSelect={(option) => setComponent(option?.value)}
        />
        <SelectFilter
          label="Można utworzyć z"
          variant="single-select"
          initiallySelectedValues={descendantOf ? [descendantOf] : []}
          options={componentOptions}
          onSelect={(option) => setDescendantOf(option?.value)}
        />
        <SelectFilter
          label="Wymagane do utworzenia"
          variant="single-select"
          initiallySelectedValues={ancestorOf ? [ancestorOf] : []}
          options={unitOptions}
          onSelect={(option) => setAncestorOf(option?.value)}
        />
        <Sort
          type="button"
          label="Sortuj"
          value={{ field: sort?.field ?? "", order: sort?.order ?? "" }}
          options={sortOptions}
          onChange={(value) => setSort(value ? (value as UnitTemplateSort) : undefined)}
        />
      </FilterList>

      <QuerySkeleton query={templatesQuery}>
        {({ units, count }) => (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1rem" }}>
            <span css={{ fontSize: "0.875rem", color: theme.colors.secondaryText }}>
              {count} {count === 1 ? "szablon" : "szablonów"}
            </span>
            <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
              <Table<UnitTemplateSortField>
                columns={tableColumns}
                activeSortValue={sort}
                onSort={(value) => setSort(value as UnitTemplateSort)}
                css={{ padding: "0 2rem" }}
              >
                <TableBody
                  isLoading={templatesQuery.isFetching}
                  rows={sortUnitTemplates(units, sort).map((template) => ({
                    id: template.id,
                    cellsContent: getTableRowCellsContent(template),
                  }))}
                />
              </Table>
            </div>
          </div>
        )}
      </QuerySkeleton>
    </Page>
  );
};

const toOption = (value: string) => ({ label: value, value });

const sortUnitTemplates = (templates: UnitTemplate[], sort: UnitTemplateSort): UnitTemplate[] => {
  if (!sort) return templates;

  return [...templates].sort((left, right) => {
    const leftValue = getSortValue(left, sort.field);
    const rightValue = getSortValue(right, sort.field);
    const result =
      typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), "pl");

    return sort.order === "ASC" ? result : -result;
  });
};

const getSortValue = (template: UnitTemplate, field: UnitTemplateSortField): string | number => {
  if (field === "components") return unitTemplateComponentsSerializer.serialize(template.components);
  return template[field];
};
